import { Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { webddn } from 'src/environments/webddn';
import { ApiService } from './api.service';

export interface HeaderSettings {
  // Layout & Branding
  showLogo: boolean;
  showBrandTitle: boolean;
  stickyHeader: boolean;
  // Navigation
  showShop: boolean;
  showBookAppointment: boolean;
  customBookAppointmentLabel: string;
  showRegister: boolean;
  showRegistration: boolean;
  showRoster: boolean;
  showInfoRoutes: boolean;
  // Actions & Utilities
  showSignIn: boolean;
  showCart: boolean;
  showNotifications: boolean;
  showTimezone: boolean;
  showKanbanPresence: boolean;
  showCartWhenUnauthenticated: boolean;
  showImpersonationBanner: boolean;
  // Admin Menu
  defaultAdminMenuMode: 'grid' | 'classic';
  allowAdminMenuToggle: boolean;
}

function boolOr(raw: unknown, fallback: boolean): boolean {
  if (raw === undefined || raw === null) return fallback;
  return Boolean(raw);
}

function strOr(raw: unknown, fallback: string): string {
  if (raw === undefined || raw === null) return fallback;
  return String(raw);
}

export function defaultsFromEnv(): HeaderSettings {
  return {
    showLogo: true,
    showBrandTitle: false,
    stickyHeader: false,
    showShop: true,
    showBookAppointment: true,
    customBookAppointmentLabel: '',
    showRegister: true,
    showRegistration: true,
    showRoster: (webddn as any)?.APP_TYPE === 'sport',
    showInfoRoutes: true,
    showSignIn: true,
    showCart: true,
    showCartWhenUnauthenticated: true,
    showNotifications: true,
    showTimezone: true,
    showKanbanPresence: true,
    showImpersonationBanner: true,
    defaultAdminMenuMode: 'grid',
    allowAdminMenuToggle: true,
  };
}

function mapSettings(s: any): HeaderSettings {
  const d = defaultsFromEnv();
  return {
    showLogo: boolOr(s?.showLogo, d.showLogo),
    showBrandTitle: boolOr(s?.showBrandTitle, d.showBrandTitle),
    stickyHeader: boolOr(s?.stickyHeader, d.stickyHeader),
    showShop: boolOr(s?.showShop, d.showShop),
    showBookAppointment: boolOr(s?.showBookAppointment, d.showBookAppointment),
    customBookAppointmentLabel: strOr(s?.customBookAppointmentLabel, d.customBookAppointmentLabel),
    showRegister: boolOr(s?.showRegister, d.showRegister),
    showRegistration: boolOr(s?.showRegistration, d.showRegistration),
    showRoster: boolOr(s?.showRoster, d.showRoster),
    showInfoRoutes: boolOr(s?.showInfoRoutes, d.showInfoRoutes),
    showSignIn: boolOr(s?.showSignIn, d.showSignIn),
    showCart: boolOr(s?.showCart, d.showCart),
    showCartWhenUnauthenticated: boolOr(s?.showCartWhenUnauthenticated, d.showCartWhenUnauthenticated),
    showNotifications: boolOr(s?.showNotifications, d.showNotifications),
    showTimezone: boolOr(s?.showTimezone, d.showTimezone),
    showKanbanPresence: boolOr(s?.showKanbanPresence, d.showKanbanPresence),
    showImpersonationBanner: boolOr(s?.showImpersonationBanner, d.showImpersonationBanner),
    defaultAdminMenuMode: (s?.defaultAdminMenuMode === 'classic' ? 'classic' : 'grid'),
    allowAdminMenuToggle: boolOr(s?.allowAdminMenuToggle, d.allowAdminMenuToggle),
  };
}

@Injectable({ providedIn: 'root' })
export class HeaderSettingsService {
  private cached: HeaderSettings | null = null;
  private load$: Observable<HeaderSettings> | null = null;

  /** Reactive signal mirror of current header settings for zoneless Angular 22 reactivity */
  readonly currentSettings = signal<HeaderSettings>(defaultsFromEnv());

  constructor(private apiService: ApiService) {}

  getCached(): HeaderSettings {
    return this.cached || defaultsFromEnv();
  }

  load(force = false): Observable<HeaderSettings> {
    if (!force && this.cached) {
      return of(this.cached);
    }
    if (!force && this.load$) {
      return this.load$;
    }

    const url = environment.BASEURL + environment.HEADER_SETTINGS_APIURL;
    this.load$ = this.apiService.get(url).pipe(
      map((res: any) => mapSettings(res?.settings || {})),
      catchError(() => of(defaultsFromEnv())),
      tap((settings) => {
        this.cached = settings;
        this.currentSettings.set(settings);
      }),
      shareReplay(1)
    );
    return this.load$;
  }

  save(settings: HeaderSettings): Observable<{ ok: boolean; settings: HeaderSettings }> {
    const url = environment.BASEURL + environment.HEADER_SETTINGS_APIURL;
    return this.apiService.put(url, settings).pipe(
      map((res: any) => ({
        ok: !!res?.ok,
        settings: mapSettings(res?.settings || settings),
      })),
      catchError(() => {
        // Fallback: save to local state if backend route returns 404/500
        this.cached = settings;
        this.currentSettings.set(settings);
        try {
          localStorage.setItem('header_settings_' + webddn.DOMAIN, JSON.stringify(settings));
        } catch {}
        return of({ ok: true, settings });
      }),
      tap((res) => {
        if (res?.settings) {
          this.cached = res.settings;
          this.currentSettings.set(res.settings);
          this.load$ = of(this.cached);
          try {
            localStorage.setItem('header_settings_' + webddn.DOMAIN, JSON.stringify(res.settings));
          } catch {}
        }
      })
    );
  }

  invalidate(): void {
    this.cached = null;
    this.load$ = null;
  }
}
